interface RestaurantChoice {
  id: string | number
  currency?: string
}

/**
 * Makes `restaurant` the one the app works on: every API call is prefixed with
 * its id, and amounts are stored and sent in its currency's minor units (see
 * shared/utils/price).
 */
export function rememberRestaurant(restaurant: RestaurantChoice) {
  localStorage.setItem('restaurantId', String(restaurant.id))
  localStorage.setItem('currency', String(restaurant.currency ?? 'PLN'))
}

/**
 * The venue to open at login: the one chosen last time if this account still
 * has it, otherwise its first. Always taking the first locked an owner of
 * several venues out of every other one.
 */
export function pickRestaurant<T extends RestaurantChoice>(
  restaurants: T[]
): T | undefined {
  const last = localStorage.getItem('restaurantId')
  return (
    restaurants.find(restaurant => String(restaurant.id) === last) ??
    restaurants[0]
  )
}

/**
 * Switches the whole app to another venue.
 *
 * By reloading, because the venue is read once by too many places (query
 * keys, the persisted stores and their storage keys, the HTTP prefix) for an
 * in-place switch to reach them all, and one it missed would show one venue's
 * data under another's name.
 */
export function switchRestaurant(restaurant: RestaurantChoice) {
  rememberRestaurant(restaurant)
  window.location.reload()
}

/**
 * Opens the venue picked at login, on a fresh page load for the same reason as
 * `switchRestaurant`. The stores were created on the login page, holding no
 * venue or the account signed out before this one, and the query cache still
 * has that account's venue list.
 */
export function enterRestaurant(restaurant: RestaurantChoice, path: string) {
  rememberRestaurant(restaurant)
  window.location.assign(path)
}
